import type { ReactNode } from 'react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { LockClosedIcon, PlusIcon } from '@heroicons/react/24/outline';
import { PlanScope, PlannerPlan } from '@/lib/types';
import useIsViewer from '@/lib/hooks/use-is-viewer';
import usePlannerPlansStore from '@/utils/use-planner-plans';
import { PlannerAction } from '@/pages/planner/planner-machine';
import { SCOPE_DOT_COLOR, SCOPE_LABEL } from '@/pages/planner/plan-scope';

interface PlanScopeTabsProps {
  date: string;
  // Which day slot the panel below is showing: the plan's scope on the view
  // screen, or the slot being created on the choice screen.
  activeScope: PlanScope;
  // True when the active slot is empty and being created (choice screen)
  // rather than viewed.
  isCreating: boolean;
  dispatch: (action: PlannerAction) => void;
  children: ReactNode;
}

interface TabPillProps {
  scope: PlanScope;
  isActive: boolean;
  onClick: () => void;
  // Plan name, or undefined for an add-chip (empty slot).
  name?: string;
  locked?: boolean;
}

function TabPill({ scope, isActive, onClick, name, locked }: TabPillProps) {
  return (
    <Flex
      as='button'
      type='button'
      onClick={onClick}
      align='center'
      gap={2}
      px={3}
      py={1.5}
      borderRadius='lg'
      borderWidth='1px'
      borderColor={isActive ? 'fg.strong' : 'transparent'}
      bg={isActive ? 'bg.panel' : 'transparent'}
      shadow={isActive ? 'sm' : undefined}
      cursor='pointer'
      transition='background-color .12s, border-color .12s'
      _hover={isActive ? undefined : { bg: 'bg.panel' }}
      minW={0}
    >
      {name === undefined ? (
        <Icon as={PlusIcon} w={3.5} h={3.5} color='fg.muted' flexShrink={0} />
      ) : (
        <Box w={2} h={2} borderRadius='full' bg={SCOPE_DOT_COLOR[scope]} flexShrink={0} />
      )}
      <Text
        fontSize='label'
        fontWeight='700'
        textTransform='uppercase'
        letterSpacing='label'
        color='fg.muted'
        whiteSpace='nowrap'
      >
        {SCOPE_LABEL[scope]}
      </Text>
      {name !== undefined && (
        <Text
          fontSize='sm'
          fontWeight='600'
          color={isActive ? 'fg.strong' : 'fg.muted'}
          lineClamp={1}
          minW={0}
        >
          {name}
        </Text>
      )}
      {locked && <Icon as={LockClosedIcon} w={3.5} h={3.5} color='fg.subtle' flexShrink={0} />}
    </Flex>
  );
}

// Tab strip over the working panel splitting a date into its two slots: the
// shared workspace plan and the caller's private "My Plan". Existing plans
// render as switchable tabs (a lock marks a workspace plan the caller may
// not edit); an empty slot renders as a "+" chip that opens the choice
// screen for that scope — hidden from viewers, who cannot create plans.
// When there is nothing to switch to and nothing to add, the strip
// disappears and `children` (the working panel card) render bare.
export default function PlanScopeTabs({
  date,
  activeScope,
  isCreating,
  dispatch,
  children,
}: PlanScopeTabsProps) {
  const isViewer = useIsViewer();
  const items = usePlannerPlansStore((s) => s.items);

  const dayPlans = items.filter((plan) => plan.plan_date.substring(0, 10) === date);
  const byScope: Partial<Record<PlanScope, PlannerPlan>> = {};
  for (const plan of dayPlans) byScope[plan.scope] = plan;

  // Viewers get no add-chips, so with a single plan (or none) there is
  // nothing to switch — skip the strip entirely.
  if (isViewer && dayPlans.length <= 1) return <>{children}</>;
  if (!isViewer && dayPlans.length === 0 && !isCreating) return <>{children}</>;

  const scopes: PlanScope[] = ['workspace', 'personal'];

  return (
    <Box bg='bg.subtle' borderRadius='lg' p={1.5}>
      <Flex gap={1.5} px={1} pb={1.5} flexWrap='wrap'>
        {scopes.map((scope) => {
          const plan = byScope[scope];
          if (plan) {
            return (
              <TabPill
                key={scope}
                scope={scope}
                name={plan.name}
                locked={scope === 'workspace' && !plan.can_edit && !isViewer}
                isActive={activeScope === scope && !isCreating}
                onClick={() => dispatch({ type: 'GO_TO', screen: { name: 'view', date, scope } })}
              />
            );
          }
          if (isViewer) return null;
          return (
            <TabPill
              key={scope}
              scope={scope}
              isActive={activeScope === scope && isCreating}
              onClick={() => dispatch({ type: 'GO_TO', screen: { name: 'choice', date, scope } })}
            />
          );
        })}
      </Flex>
      {children}
    </Box>
  );
}
